import { Component } from '@theme/component';

/**
 * `<echt-drop-countdown>` — keeps the drop timer's four values current.
 *
 * Progressive enhancement only. The section renders the countdown with the
 * remaining time already in the HTML, computed in Liquid from `'now'` against
 * `launch_at`, so with this file blocked the shopper still sees a correct
 * D/H/M/S at page load — and, when the drop has passed and the merchandiser
 * asked for it, sees no timer at all because Liquid did not render one. All
 * this adds is the second hand.
 *
 * It ticks once a second and never animates: a countdown that moved would be
 * motion the shopper cannot stop, so `[data-motion-paused]` and
 * `prefers-reduced-motion` have nothing here to suppress. The text keeps
 * changing under both, which is the point of a countdown and is not motion in
 * the WCAG 2.2.2 sense — it is neither moving, blinking nor scrolling.
 *
 * `aria-live="off"` sits on every value in the markup, so none of these writes
 * is announced. Four numbers a second is unusable; the launch date is given
 * once, in a visually hidden sentence, instead.
 *
 * @typedef {object} CountdownRefs
 * @property {HTMLElement} days - The two-digit day value.
 * @property {HTMLElement} hours - The two-digit hour value.
 * @property {HTMLElement} minutes - The two-digit minute value.
 * @property {HTMLElement} seconds - The two-digit second value.
 *
 * @extends {Component<CountdownRefs>}
 */
class EchtDropCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  /** The launch instant in epoch milliseconds, or NaN when it cannot be read. */
  #target = NaN;

  connectedCallback() {
    super.connectedCallback();

    // Liquid writes the ISO 8601 string with its offset, which Date parses in
    // every browser. Safari refuses the space-separated form the live theme
    // accepted, which is why the setting is specified as ISO and normalised
    // here rather than guessed at.
    const raw = (this.dataset.launch ?? '').trim();
    this.#target = new Date(raw.replace(' ', 'T')).getTime();

    this.#tick();
    // No interval when the date is unreadable or already spent: #tick has
    // already written 00 and hidden the element if it was asked to, and there
    // is nothing left for a timer to do.
    if (Number.isFinite(this.#target) && this.#target > Date.now()) {
      this.#interval = setInterval(() => this.#tick(), 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #stop() {
    if (this.#interval != null) clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Writes the current remainder into the four values.
   *
   * At or past zero every unit reads 00 — the honest state for a drop that has
   * landed — and, when `data-hide-on-end` is set, the element goes away. `hidden`
   * rather than `style.display`: it is the attribute the rest of this theme
   * toggles, and it survives a Section Rendering API re-render as markup.
   */
  #tick() {
    const { days, hours, minutes, seconds } = this.refs;
    if (!days || !hours || !minutes || !seconds) return;

    const distance = this.#target - Date.now();

    if (!Number.isFinite(distance) || distance <= 0) {
      for (const el of [days, hours, minutes, seconds]) el.textContent = '00';
      if (this.hasAttribute('data-hide-on-end')) this.hidden = true;
      this.#stop();
      return;
    }

    const total = Math.floor(distance / 1000);
    days.textContent = pad(Math.floor(total / 86400));
    hours.textContent = pad(Math.floor(total / 3600) % 24);
    minutes.textContent = pad(Math.floor(total / 60) % 60);
    seconds.textContent = pad(total % 60);
  }
}

/**
 * Two digits, matching what Liquid rendered — and more than two once a drop is
 * further out than 99 days, because truncating the day count would be a lie.
 *
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return value < 10 ? `0${value}` : String(value);
}

if (!customElements.get('echt-drop-countdown')) customElements.define('echt-drop-countdown', EchtDropCountdown);
